import type { RefObject } from 'react'
import { useEffect } from 'react'

import { sleep } from '@/helpers/sleep'

type Options = {
  activationRate: number
}

/**
 * Takes a ref to an input field, and over time updates its placeholder value
 * to create the effect of the placeholder being "typed" in. Progresses through
 * multiple placeholder strings.
 */
const useAnimatedPlaceholder = (
  inputRef: RefObject<HTMLInputElement | null>,
  placeholders?: string[],
  options?: Options
) => {
  const activationRate = options?.activationRate || 1

  useEffect(() => {
    if (Math.random() > activationRate || !placeholders) {
      return
    }

    let keepGoing = true

    const typeOutThing = async (thing: string) => {
      for (let i = 0; i <= thing.length; i++) {
        if (!keepGoing || !inputRef.current) return

        inputRef.current.placeholder = thing.substring(0, i)

        await sleep(100)
      }
    }

    const backspaceThing = async () => {
      while (inputRef.current?.placeholder.length) {
        if (!keepGoing) return

        inputRef.current.placeholder = inputRef.current.placeholder.substring(
          0,
          inputRef.current.placeholder.length - 1
        )
        await sleep(50)
      }
    }

    void (async () => {
      for (let i = 0; i < placeholders.length; i++) {
        // Index is guaranteed valid by loop condition (i < length)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await typeOutThing(placeholders[i]!)
        await sleep(1500)

        if (i !== placeholders.length - 1) {
          // Don't backspace out of the last one
          await backspaceThing()
        }
      }
    })()

    return () => {
      keepGoing = false
    }
    // Because the placeholders array is a reference type, and we usually feed it
    // in via feature flag which will refetch on load and periodically and thus
    // produce a new object even though each item may remain the same, we exclude
    // it here to prevent retriggering the animation. We could seralize it for
    // the dep array here, but that comes with a perf cost and generally we can
    // depend on this value being present on first render.
    // oxlint-disable-next-line react/exhaustive-deps
  }, [inputRef, activationRate])
}

export default useAnimatedPlaceholder
